import React from 'react';
import { Box } from '@mui/material';
import ListingStatusChip from '../../ListingStatusChip/ListingStatusChip';
import { LD, formatAcres, formatCurrency, propertyTypeLabel } from './tokens';

/** The MLS fields the Details row reads; every one is optional on a feed. */
export interface ListingDetailsFields {
  propertyType?: string | null;
  yearBuilt?: number | null;
  lotSizeAcres?: number | null;
  area?: number | null;
  listingPrice?: number | null;
  stories?: number | null;
  garageSpaces?: number | null;
  associationFee?: number | string | null;
  associationFeeFrequency?: string | null;
  taxAnnualAmount?: number | string | null;
  taxYear?: number | null;
  subdivision?: string | null;
  elementarySchool?: string | null;
  middleSchool?: string | null;
  highSchool?: string | null;
  heating?: string[] | null;
  cooling?: string[] | null;
  county?: string | null;
  isActive?: string | null;
  mlsStatus?: string | null;
}

/** "Single family · Built 2016 · 0.20 ac", from whatever the feed supplied. */
export const detailsSummary = (fields: ListingDetailsFields): string[] => {
  const parts: string[] = [];
  const type = propertyTypeLabel(fields.propertyType);
  if (type) parts.push(type);
  if (fields.yearBuilt) parts.push(`Built ${fields.yearBuilt}`);
  if (fields.lotSizeAcres && fields.lotSizeAcres > 0) {
    parts.push(`${formatAcres(fields.lotSizeAcres)} ac`);
  }
  return parts;
};

const toNumber = (value: number | string | null | undefined): number =>
  Number(value) || 0;

const joinList = (values?: string[] | null): string | undefined =>
  values && values.length > 0 ? values.join(', ') : undefined;

interface DetailsPanelProps {
  fields: ListingDetailsFields;
  dom: number;
  mlsNumber?: string;
}

/** Key facts about the home, one per line, skipping anything the MLS left blank. */
const DetailsPanel: React.FC<DetailsPanelProps> = ({
  fields,
  dom,
  mlsNumber,
}) => {
  const area = fields.area || 0;
  const price = fields.listingPrice || 0;
  const hoa = toNumber(fields.associationFee);
  const tax = toNumber(fields.taxAnnualAmount);
  const schools = [
    fields.elementarySchool,
    fields.middleSchool,
    fields.highSchool,
  ]
    .filter(Boolean)
    .join(' · ');

  const rows: Array<[string, string | undefined]> = [
    ['Type', propertyTypeLabel(fields.propertyType)],
    ['Year built', fields.yearBuilt ? String(fields.yearBuilt) : undefined],
    ['Living area', area ? `${area.toLocaleString()} sqft` : undefined],
    [
      'Lot',
      fields.lotSizeAcres && fields.lotSizeAcres > 0
        ? `${formatAcres(fields.lotSizeAcres)} acres`
        : undefined,
    ],
    [
      'Price per sqft',
      area && price ? formatCurrency(Math.round(price / area)) : undefined,
    ],
    ['Stories', fields.stories ? String(fields.stories) : undefined],
    [
      'Garage',
      fields.garageSpaces ? `${fields.garageSpaces} spaces` : undefined,
    ],
    [
      'HOA',
      hoa
        ? `${formatCurrency(hoa)}${
            fields.associationFeeFrequency
              ? ` ${fields.associationFeeFrequency.toLowerCase()}`
              : ''
          }`
        : undefined,
    ],
    [
      'Property tax',
      tax
        ? `${formatCurrency(tax)}/yr${
            fields.taxYear ? ` (${fields.taxYear})` : ''
          }`
        : undefined,
    ],
    ['Heating', joinList(fields.heating)],
    ['Cooling', joinList(fields.cooling)],
    ['Subdivision', fields.subdivision || undefined],
    ['County', fields.county || undefined],
    ['Schools', schools || undefined],
    ['Days on market', dom > 0 ? String(dom) : undefined],
    ['MLS#', mlsNumber],
  ];
  const shown = rows.filter((row): row is [string, string] => Boolean(row[1]));

  return (
    <Box data-testid="details-panel">
      <ListingStatusChip
        status={fields.isActive}
        mlsStatus={fields.mlsStatus}
      />
      {shown.map(([label, value], index) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            fontSize: '13.5px',
            lineHeight: 'normal',
            py: '6px',
            color: LD.ink2,
            borderBottom:
              index === shown.length - 1 ? 'none' : `1px dashed ${LD.line}`,
          }}
        >
          <span>{label}</span>
          <Box component="b" sx={{ color: LD.ink, textAlign: 'right' }}>
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DetailsPanel;
